<div class="page-container">
  @if (leadLoading()) {
    <div class="loading-container flex flex-column flex-align-center p-lg">
      <app-spinner />
    </div>
  } @else if (notFound()) {
    <app-card>
      <app-card-content>
        <p>{{ 'leads.detail.notFoundError' | translate }}</p>
      </app-card-content>
    </app-card>
  } @else if (lead(); as l) {
    <app-card>
      <app-card-header>
        <div class="flex flex-align-center flex-justify-space-between gap-sm">
          <app-card-title>
            <div class="flex flex-column gap-xs">
              <span>{{ l.contact_name || ('leads.detail.untitled' | translate) }}</span>
              @if (address(); as a) {
                <a class="subtitle" [routerLink]="['/map/addresses', a.id]" data-testid="lead-address-link">{{ a.formatted_address }}</a>
              }
            </div>
          </app-card-title>
          <app-lead-stage-chip [stage]="l.stage" />
        </div>
      </app-card-header>

      <app-card-content>
        <div class="contact-block flex flex-column gap-xs">
          @if (l.contact_phone) {
            <a [href]="'tel:' + l.contact_phone" class="contact-link">
              <app-icon name="phone" [size]="14" /> {{ l.contact_phone }}
            </a>
          }
          @if (l.contact_email) {
            <a [href]="'mailto:' + l.contact_email" class="contact-link">
              <app-icon name="mail" [size]="14" /> {{ l.contact_email }}
            </a>
          }
        </div>

        @if (lineItems().length > 0) {
          <div class="estimate-block">
            <h4>{{ 'leads.detail.estimate' | translate }}</h4>
            <table class="estimate-table" data-testid="estimate-table">
              <tbody>
                @for (item of lineItems(); track item.service_id) {
                  <tr>
                    <td>{{ item.label }}</td>
                    <td>@if (item.unit_price > 0) { ×{{ item.quantity }} }</td>
                    <td class="estimate-total-col">@if (item.unit_price > 0) { {{ item.line_total.toFixed(2) }} }</td>
                  </tr>
                }
              </tbody>
            </table>
            @if (l.estimated_value) {
              <div class="estimate-grand-total">{{ 'leads.detail.grandTotal' | translate }}: {{ l.estimated_value.toFixed(2) }} {{ l.currency }}</div>
            }
          </div>
        }

        @if (tags().length > 0) {
          <div class="tag-block flex gap-xs" data-testid="lead-tags">
            @for (tag of tags(); track tag.id) {
              <app-tag-chip [tag]="tag" />
            }
          </div>
        }

        @if (l.notes) {
          <p class="lead-notes">{{ l.notes }}</p>
        }

        <app-attachment-strip entityType="lead" [entityId]="l.id" />

        <div class="section-divider"></div>

        @if (canAssign) {
          <div class="assignee-block" data-testid="assignee-block">
            <form [formGroup]="assigneeForm">
              <formly-form [form]="assigneeForm" [fields]="assigneeFields" [model]="assigneeModel" />
            </form>
          </div>
        }

        @if (canUpdate) {
          <div class="edit-block" data-testid="edit-block">
            <form [formGroup]="editForm">
              <formly-form [form]="editForm" [fields]="editFields" [model]="editModel" />
            </form>
            <app-button variant="filled" [disabled]="savingEdit()" (click)="saveEdit()" data-testid="save-lead-edit-button">
              {{ 'leads.detail.save' | translate }}
            </app-button>
          </div>
        }

        <div class="section-divider"></div>

        <div class="appointments-block">
          <h4>{{ 'leads.detail.appointments' | translate }}</h4>
          @if (appointments().length === 0) {
            <p class="empty-state">{{ 'leads.detail.noAppointments' | translate }}</p>
          } @else {
            <ul class="appointment-list">
              @for (appt of appointments(); track appt.id) {
                <li class="appointment-row" [attr.data-testid]="'appointment-row-' + appt.id">
                  <app-icon name="calendar" [size]="14" />
                  {{ appt.scheduled_start | dateTimezone }} — {{ appt.status }}
                </li>
              }
            </ul>
          }
          @if (canBookAppointment) {
            <app-button variant="outlined" (click)="openBookAppointment()" data-testid="book-appointment-button">
              {{ 'leads.detail.bookAppointment' | translate }}
            </app-button>
          }
        </div>
      </app-card-content>
    </app-card>

    <div class="detail-actions">
      <app-button variant="outlined" (click)="exportLead()" data-testid="export-lead-button">
        <app-icon name="download" [size]="16" />
        {{ 'leads.detail.export' | translate }}
      </app-button>
    </div>
  }
</div>

@if (showAppointmentModal()) {
  <app-modal-dialog (backdropClick)="closeBookAppointment()">
    <div header>
      <div class="heading-md">{{ 'leads.detail.bookAppointment' | translate }}</div>
    </div>
    <div content>
      <form [formGroup]="appointmentForm">
        <formly-form [form]="appointmentForm" [fields]="appointmentFields" [model]="appointmentModel" />
      </form>
    </div>
    <div footer>
      <app-button variant="text" (click)="closeBookAppointment()" data-testid="cancel-book-appointment-button">
        {{ 'interactions.form.cancel' | translate }}
      </app-button>
      <app-button variant="filled" color="primary" [disabled]="bookingAppointment()" (click)="bookAppointment()" data-testid="confirm-book-appointment-button">
        {{ 'leads.detail.bookAppointment' | translate }}
      </app-button>
    </div>
  </app-modal-dialog>
}
